import Modal from "components/Modal";
import { useAlertsContext } from "components/Alerts/AlertsContext";
import Dropdown from "components/Dropdown";
import { useEffect, useState } from 'react';
import { CreateAlertSchema } from '../../validations/alerts';
import ConfirmDeleteModal from "components/Modal/ConfirmDeleteModal";
import { useUniqueQuestionList } from 'hooks/questionHooks';
import { createAlert } from 'api/alerts/index';
import loadingGif from 'images/loader2.webp';
import { useAlertMethods } from 'hooks/alertHooks';
import SearchDropdown from "components/Dropdown/SearchDropdown";

const CreateAlertModal = () => {

    const { addAlert } = useAlertMethods();

    const [selectedQuery, setSelectedQuery] = useState(null);
    const [selectedColumn, setSelectedColumn] = useState(null);
    const [selectedCondition, setSelectedCondition] = useState(null);
    const [selectedEmail, setSelectedEmail] = useState("");
    const [selectedThreshold, setSelectedThreshold] = useState("");

    const [loading, setLoading] = useState(false);
    const [formErrors, setFormErrors] = useState({ errorCount: 0 });
    const { showModal, setShowModal } = useAlertsContext();

    const questions = useUniqueQuestionList(1);

    const conditionOptions = [
        {
            label: "Above",
            value: ">",
        },
        {
            label: "Below",
            value: "<",
        },
        {
            label: "Equal",
            value: "=",
        },
    ]

    const onQueryChange = (data) => {
        setSelectedQuery(data);
        setSelectedColumn(null);
    }

    const verifyPayload = async (payload) => {
        try {
            return CreateAlertSchema.validate(payload, { abortEarly: false });
        } catch (err) {
            console.log("Alert Validation Error: ", err);
            throw new Error(err)
        }
    }

    const handleSaveAlert = async () => {
        setLoading(true);
        try {
            const payload = {
                query_id: parseInt(selectedQuery?.value) || null,
                value_column: selectedColumn?.value,
                condition: selectedCondition?.value,
                threshold: selectedThreshold,
                email: selectedEmail,
            }

            const verifiedPayload = await verifyPayload(payload);
            const result = await createAlert(verifiedPayload);
            addAlert({
                query_id: parseInt(selectedQuery?.value),
                question: selectedQuery?.label,
                value_column: selectedColumn?.value,
                condition_type: selectedCondition?.value,
                threshold: selectedThreshold,
                email: selectedEmail,
                created_at: Date.now()
            })
            setLoading(false);
            setFormErrors({ errorCount: 0 });
            setShowModal(false);

        } catch (err) {
            let errorCount = 0
            const errObj = {};
            err.inner.forEach(error => {
                errObj[error.path] = error.message;
                errorCount += 1
            })
            setFormErrors({ ...errObj, errorCount });

            console.log("Error", errObj)
        } finally {
            setLoading(false);
        }
    }

    const resetModalStates = () => {
        setLoading(false);
        setFormErrors({ errorCount: 0 })
        setShowModal(false);
    }

    useEffect(() => {
        // flush the states
        return () => {
            resetModalStates();
        }
    }, []);

    return (
        <Modal
            position="top-right"
            className="w-[40rem] px-8 py-10 bg-primary-3 text-white rounded-lg text-left"
            open={showModal}
        >
            <h1>Create Alert</h1>
            <hr className="mt-2" />

            {
                formErrors.errorCount > 0 && (
                    <div className="mt-6 py-3 px-2 rounded bg-error-1 flex items-center justify-center">
                        <span className="text-red-600">Please correct the errors</span>
                    </div>
                )
            }

            {
                selectedQuery?.label ? (
                    <p className="mt-6">Alert me when <b>"{selectedQuery?.label}"</b> graph raises to above, equal to or below specific value.</p>
                ) : (
                    <p className="mt-6">Please select a query for which you want to create an alert.</p>
                )
            }


            {/* Select Query */}
            <div className="w-full mt-6">
                <SearchDropdown onChange={onQueryChange} selectedOption={selectedQuery} options={questions} />

                <span className="text-red-500 mt-2">{formErrors?.query_id}</span>
            </div>

            {/* Select columns for a selected query*/}
            <div className="mt-6 flex gap-6">
                <div className="w-full">
                    <Dropdown
                        hasError={formErrors?.value_column}
                        selectedOption={selectedColumn}
                        onChange={setSelectedColumn}
                        options={selectedQuery?.columns || []}
                    />
                    <span className="text-red-500 mt-2">{formErrors?.value_column}</span>
                </div>

            </div>

            {/* Alert Trigger */}
            <div className="mt-4 flex gap-6">

                <div className="w-full">
                    <Dropdown
                        hasError={formErrors?.condition}
                        selectedOption={selectedCondition}
                        onChange={setSelectedCondition}
                        options={conditionOptions}
                    />
                    <span className="text-red-500 mt-2">{formErrors?.condition}</span>
                </div>

                <div className="w-full">
                    <div className="relative w-full">
                        <input
                            type="text"
                            value={selectedThreshold}
                            onChange={(e) => setSelectedThreshold(e.target.value)}
                            className={`w-full p-3 rounded bg-[#0D1B2A] border ${formErrors?.threshold ? "border-red-500" : "border-[#382F5C]"}`}
                            placeholder="ThreshHold"
                        />
                    </div>
                    <span className="text-red-500 mt-2">{formErrors?.threshold}</span>
                </div>
            </div>

            <div className="mt-6">
                <label
                    htmlFor="email"
                    className={`block text-sm `}
                >
                    Email address
                </label>
                <input
                    type="email"
                    id="create-alert-email"
                    value={selectedEmail}
                    onChange={(e) => setSelectedEmail(e.target.value)}
                    className={`w-full mt-2 p-3 rounded bg-[#0D1B2A] border ${formErrors?.email ? "border-red-500" : "border-[#382F5C]"}`}
                    placeholder="Enter email id where you want to receive alerts."
                />
                <span className="text-red-500 mt-2">{formErrors?.email}</span>
            </div>

            <div className="flex justify-end mt-6 gap-4">
                <button disabled={loading} className="py-2 text-gray-300 px-4 border border-gray-200 rounded text-black" onClick={resetModalStates}>Cancel</button>
                <button
                    disabled={loading}
                    className="py-2 bg-primary-1 text-gray-400 px-4 rounded text-white flex gap-2 items-center disabled:bg-gray-600 disabled:text-gray-300"
                    onClick={handleSaveAlert}
                >
                    {
                        loading && (
                            <img src={loadingGif} alt="loading" className={`h-4 w-4`} />
                        )
                    }
                    <span>
                        {loading ? "Saving" : "Save"}
                    </span>
                </button>
            </div>
        </Modal>
    );
}

export default CreateAlertModal